import React, { useState, useEffect } from 'react';
import {
  Box,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  useMediaQuery,
  useTheme,
  Divider,
  Stack,
  Avatar,
  Collapse,
  alpha,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Close as CloseIcon,
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Assignment as AssignmentIcon,
  LocalHospital as LocalHospitalIcon,
  Settings as SettingsIcon,
  ExitToApp as LogoutIcon,
  ExpandLess,
  ExpandMore,
  AccountCircle as AccountIcon,
  AdminPanelSettings as AdminIcon,
  Science as ProtocolIcon,
  Rule as RuleIcon,
  ChevronLeft as ChevronLeftIcon,
  HelpOutline as HelpIcon,
  EventNote as AppointmentIcon,
  FormatListBulleted as WorklistIcon,
  CheckCircle as ReadinessIcon,
  ShoppingCart as OrdersIcon,
  Psychology as AIInsightsIcon,
  Groups as SurgeonsIcon,
  Gavel as ComplianceIcon,
  Hub as IntegrationsIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import VirtualPATLogo, { SidebarLogo } from './virtualpat-theme/VirtualPATLogo';

const SIDEBAR_WIDTH = 280;
const SIDEBAR_WIDTH_COLLAPSED = 72;

function VirtualPATSidebar({ user, onLogout, appMode = 'standard', mobileOpen, onDrawerToggle }) {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.down('lg'));

  const [internalMobileOpen, setInternalMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [expandedItems, setExpandedItems] = useState({});

  // Use props for mobile or internal state
  const isOpen = mobileOpen !== undefined ? mobileOpen : internalMobileOpen;
  const toggleDrawer = onDrawerToggle || (() => setInternalMobileOpen(!internalMobileOpen));

  // Auto-collapse on tablet
  useEffect(() => {
    if (isTablet && !isMobile) {
      setCollapsed(true);
    }
  }, [isTablet, isMobile]);

  const handleCollapse = () => {
    setCollapsed(!collapsed);
  };

  const handleNavigate = path => {
    navigate(path);
    if (isMobile) {
      toggleDrawer();
    }
  };

  const handleExpandClick = item => {
    setExpandedItems(prev => ({
      ...prev,
      [item]: !prev[item],
    }));
  };

  const isActive = path => {
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };

  // Define role groups
  const userRole = user?.role || '';
  const isAdmin = ['ADMIN', 'HOSPITAL_ADMIN', 'ASC_ADMIN', 'BUSINESS_ADMIN'].includes(userRole);
  const isClinical = ['PHYSICIAN', 'NURSE', 'RESIDENT', 'SURGEON', 'ANESTHESIOLOGIST', 'CLINICIAN', 'TECH'].includes(userRole);
  
  // Base menu items for all users
  const baseMenuItems = appMode === 'asc'
    ? [
        {
          title: 'Worklist',
          path: '/asc/patient-management',
          icon: <WorklistIcon />,
          subtitle: 'My Patients',
          roles: 'all', // Available to all
        },
        {
          title: 'Readiness',
          path: '/asc/readiness',
          icon: <ReadinessIcon />,
          subtitle: 'Tasks',
          roles: 'all',
        },
        {
          title: 'Orders',
          path: '/asc/orders',
          icon: <OrdersIcon />,
          subtitle: 'Queue',
          roles: 'clinical', // Clinical staff only
        },
        {
          title: 'Protocols',
          path: '/asc/protocol-management',
          icon: <ProtocolIcon />,
          subtitle: 'ASA + Rules',
          roles: 'clinical',
        },
        // Unified: remove duplicate AI Insights entry in ASC mode
        {
          title: 'Surgeons',
          path: '/asc/surgeon-management',
          icon: <SurgeonsIcon />,
          subtitle: 'Preferences',
          roles: 'admin', // Admin only
        },
        {
          title: 'Audit',
          path: '/asc/audit',
          icon: <ComplianceIcon />,
          subtitle: 'Compliance',
          roles: 'admin',
        },
        {
          title: 'Integrations',
          path: '/asc/integrations',
          icon: <IntegrationsIcon />,
          subtitle: 'FHIR',
          roles: 'admin',
        },
      ]
    : [
        {
          title: 'Worklist',
          path: '/patient-management',
          icon: <WorklistIcon />,
          subtitle: 'My Patients',
          roles: 'all',
        },
        {
          title: 'Readiness',
          path: '/readiness',
          icon: <ReadinessIcon />,
          subtitle: 'Tasks',
          roles: 'all',
        },
        {
          title: 'Orders',
          path: '/orders',
          icon: <OrdersIcon />,
          subtitle: 'Queue',
          roles: 'clinical',
        },
        {
          title: 'Protocols',
          path: '/protocols',
          icon: <ProtocolIcon />,
          subtitle: 'ASA + Rules',
          roles: 'clinical',
        },
        // Unified: remove duplicate AI Insights entry (use Clinical Analytics below)
        {
          title: 'Surgeons',
          path: '/surgeons',
          icon: <SurgeonsIcon />,
          subtitle: 'Preferences',
          roles: 'admin',
        },
        {
          title: 'Audit',
          path: '/audit',
          icon: <ComplianceIcon />,
          subtitle: 'Compliance',
          roles: 'admin',
        },
        {
          title: 'Integrations',
          path: '/integrations',
          icon: <IntegrationsIcon />,
          subtitle: 'FHIR',
          roles: 'admin',
        },
      ];

  // Add role-specific menu items
  const roleSpecificItems = [];
  
  // Add Analytics menu based on role
  if (isAdmin) {
    roleSpecificItems.push({
      title: 'Business Analytics',
      path: '/admin/analytics',
      icon: <DashboardIcon />,
      subtitle: 'ROI & Metrics',
      roles: 'admin',
    });
  }
  
  if (isClinical || isAdmin) {
    roleSpecificItems.push({
      title: 'Clinical Analytics',
      path: '/clinical/analytics',
      icon: <AIInsightsIcon />,
      subtitle: 'Analytics & Insights',
      roles: 'clinical',
    });
  }

  // Add Communications Hub for all users
  roleSpecificItems.push({
    title: 'Communications',
    path: '/communications',
    icon: <PeopleIcon />,
    subtitle: 'Team Chat',
    roles: 'all',
  });

  // Filter menu items based on user role
  const filterMenuByRole = (items) => {
    return items.filter(item => {
      if (item.roles === 'all') return true;
      if (item.roles === 'admin' && isAdmin) return true;
      if (item.roles === 'clinical' && (isClinical || isAdmin)) return true;
      return false;
    });
  };

  // Combine and filter menu items
  const menuItems = filterMenuByRole([
    ...baseMenuItems,
    ...roleSpecificItems,
  ]);

  const bottomMenuItems = [
    {
      title: 'Settings',
      path: appMode === 'asc' ? '/asc/settings' : '/settings',
      icon: <SettingsIcon />,
    },
    ...(user?.role === 'ADMIN'
      ? [
          {
            title: 'Admin',
            path: appMode === 'asc' ? '/asc/admin' : '/admin',
            icon: <AdminIcon />,
          },
        ]
      : []),
    {
      title: 'Profile',
      path: appMode === 'asc' ? '/asc/profile' : '/profile',
      icon: <AccountIcon />,
    },
    {
      title: 'Help',
      path: '/documentation',
      icon: <HelpIcon />,
    },
  ];

  const drawerContent = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        background: 'transparent',
        position: 'relative',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background:
            theme.palette.mode === 'light'
              ? `linear-gradient(180deg, ${alpha(theme.palette.primary.main, 0.02)} 0%, ${alpha(theme.palette.secondary.main, 0.02)} 100%)`
              : `linear-gradient(180deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
          pointerEvents: 'none',
        },
      }}
    >
      {/* Logo and Brand - Devias Style */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: collapsed && !isMobile ? 1 : 3,
          height: 64,
          borderBottom: `1px solid ${theme.palette.divider}`,
          boxSizing: 'border-box',
          position: 'relative',
          overflow: 'hidden',
          width: '100%',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: collapsed && !isMobile ? '40px' : 'auto',
            height: collapsed && !isMobile ? '40px' : 'auto',
            overflow: 'hidden',
          }}
        >
          <SidebarLogo
            collapsed={collapsed && !isMobile}
            onClick={() => navigate('/')}
            sx={{
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              maxWidth: collapsed && !isMobile ? '40px' : '100%',
              height: collapsed && !isMobile ? '40px' : 'auto',
            }}
          />
        </Box>
        {isMobile && (
          <IconButton
            onClick={toggleDrawer}
            sx={{
              ml: 'auto',
              backgroundColor: alpha(theme.palette.primary.main, 0.1),
              '&:hover': {
                backgroundColor: alpha(theme.palette.primary.main, 0.2),
              },
              position: 'absolute',
              right: 8,
              zIndex: 2,
            }}
          >
            <CloseIcon />
          </IconButton>
        )}
      </Box>

      {/* Navigation Items */}
      <Box sx={{ flex: 1, overflow: 'auto', py: 1 }}>
        <List sx={{ px: 1, py: 0 }}>
          {menuItems.map(item => (
            <React.Fragment key={item.path}>
              <ListItem disablePadding sx={{ mb: 0 }}>
                <ListItemButton
                  onClick={() =>
                    item.children ? handleExpandClick(item.title) : handleNavigate(item.path)
                  }
                  selected={isActive(item.path)}
                  sx={{
                    borderRadius: 3,
                    px: collapsed && !isMobile ? 1.5 : 2,
                    py: 1,
                    mb: 0.25,
                    justifyContent: collapsed && !isMobile ? 'center' : 'flex-start',
                    transition: 'all 0.2s',
                    position: 'relative',
                    overflow: 'hidden',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background: theme.palette.primary.gradient || theme.palette.primary.main,
                      opacity: 0,
                      transition: 'opacity 0.2s',
                    },
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.primary.main, 0.08),
                      transform: `translateX(${theme.spacing(0.5)})`,
                      '&::before': {
                        opacity: 0.05,
                      },
                    },
                    '&.Mui-selected': {
                      backgroundColor: alpha(theme.palette.primary.main, 0.12),
                      borderLeft: `3px solid ${theme.palette.primary.main}`,
                      '&::before': {
                        opacity: 0.1,
                      },
                      '&:hover': {
                        backgroundColor: alpha(theme.palette.primary.main, 0.16),
                      },
                      '& .MuiListItemIcon-root': {
                        color: theme.palette.primary.main,
                      },
                      '& .MuiListItemText-primary': {
                        color: theme.palette.primary.main,
                        fontWeight: 600,
                      },
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: collapsed && !isMobile ? 'auto' : 40,
                      color: isActive(item.path)
                        ? theme.palette.primary.main
                        : theme.palette.text.secondary,
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  {(!collapsed || isMobile) && (
                    <>
                      <ListItemText
                        primary={
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: isActive(item.path) ? 600 : 500,
                            }}
                            title={item.subtitle ? `${item.title} — ${item.subtitle}` : item.title}
                          >
                            {item.title}
                          </Typography>
                        }
                      />
                      {item.badge && (
                        <Typography
                          variant="caption"
                          sx={{
                            px: 1,
                            py: 0.25,
                            borderRadius: 1,
                            bgcolor: theme.palette.primary.main,
                            color: 'white',
                          }}
                        >
                          {item.badge}
                        </Typography>
                      )}
                      {item.children &&
                        (expandedItems[item.title] ? <ExpandLess /> : <ExpandMore />)}
                    </>
                  )}
                </ListItemButton>
              </ListItem>

              {/* Nested Items */}
              {item.children && (!collapsed || isMobile) && (
                <Collapse in={expandedItems[item.title]} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    {item.children.map(child => (
                      <ListItem key={child.path} disablePadding sx={{ pl: 2, pr: 1 }}>
                        <ListItemButton
                          onClick={() => handleNavigate(child.path)}
                          selected={isActive(child.path)}
                          sx={{
                            borderRadius: 2,
                            py: 1,
                            px: 2,
                            minHeight: 36,
                            overflow: 'visible',
                            '&.Mui-selected': {
                              backgroundColor: alpha(theme.palette.primary.main, 0.08),
                            },
                          }}
                        >
                          <ListItemText
                            primary={
                              <Typography variant="body2">{child.title}</Typography>
                            }
                          />
                        </ListItemButton>
                      </ListItem>
                    ))}
                  </List>
                </Collapse>
              )}
            </React.Fragment>
          ))}
        </List>
      </Box>

      <Divider />

      {/* Bottom Menu Items */}
      <Box sx={{ py: 1 }}>
        <List sx={{ px: 1, py: 0 }}>
          {bottomMenuItems.map(item => (
            <ListItem key={item.path} disablePadding sx={{ mb: 0 }}>
              <ListItemButton
                onClick={() => handleNavigate(item.path)}
                selected={isActive(item.path)}
                sx={{
                  borderRadius: 2,
                  px: collapsed && !isMobile ? 1.5 : 2,
                  py: 0.75,
                  justifyContent: collapsed && !isMobile ? 'center' : 'flex-start',
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: collapsed && !isMobile ? 'auto' : 40,
                    color: theme.palette.text.secondary,
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                {(!collapsed || isMobile) && (
                  <ListItemText
                    primary={
                      <Typography variant="body2">{item.title}</Typography>
                    }
                  />
                )}
              </ListItemButton>
            </ListItem>
          ))}

          {/* Logout */}
          <ListItem disablePadding>
            <ListItemButton
              onClick={onLogout}
              sx={{
                borderRadius: 2,
                px: collapsed && !isMobile ? 1.5 : 2,
                py: 0.75,
                justifyContent: collapsed && !isMobile ? 'center' : 'flex-start',
                '&:hover': {
                  backgroundColor: alpha(theme.palette.error.main, 0.08),
                  '& .MuiListItemIcon-root': {
                    color: theme.palette.error.main,
                  },
                },
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: collapsed && !isMobile ? 'auto' : 40,
                  color: theme.palette.text.secondary,
                }}
              >
                <LogoutIcon />
              </ListItemIcon>
              {(!collapsed || isMobile) && (
                <ListItemText
                  primary={
                    <Typography variant="body2">Logout</Typography>
                  }
                />
              )}
            </ListItemButton>
          </ListItem>
        </List>
      </Box>

      {/* User Info - Moved to bottom */}
      {user && (
        <Box
          sx={{
            p: 1.5,
            mx: 1,
            mb: 1,
            borderRadius: 3,
            background: theme.palette.glass?.light || alpha(theme.palette.primary.main, 0.04),
            backdropFilter: 'blur(10px)',
            WebkitBackdropFilter: 'blur(10px)',
            border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
          }}
        >
          <Stack
            direction={collapsed && !isMobile ? 'column' : 'row'}
            spacing={collapsed && !isMobile ? 1 : 2}
            alignItems="center"
          >
            <Avatar
              sx={{
                width: collapsed && !isMobile ? 32 : 40,
                height: collapsed && !isMobile ? 32 : 40,
                background: theme.palette.primary.gradient || theme.palette.primary.main,
                boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.25)}`,
                fontWeight: 700,
              }}
            >
              {user.name?.charAt(0).toUpperCase()}
            </Avatar>
            {(!collapsed || isMobile) && (
              <Box>
                <Typography variant="subtitle2" fontWeight={700}>
                  {user.name}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: theme.palette.primary.main,
                    fontWeight: 600,
                  }}
                >
                  {user.role}
                </Typography>
              </Box>
            )}
          </Stack>
        </Box>
      )}

      {/* Collapse Toggle (Desktop only) */}
      {!isMobile && (
        <Box sx={{ p: 1 }}>
          <IconButton
            onClick={handleCollapse}
            sx={{
              width: '100%',
              borderRadius: 2,
              justifyContent: collapsed ? 'center' : 'flex-end',
            }}
          >
            <ChevronLeftIcon
              sx={{
                transform: collapsed ? 'rotate(180deg)' : 'none',
                transition: 'transform 0.3s',
              }}
            />
          </IconButton>
        </Box>
      )}
    </Box>
  );

  return (
    <Box
      component="nav"
      sx={{
        flexShrink: { md: 0 },
        width: {
          xs: 0,
          md: collapsed ? SIDEBAR_WIDTH_COLLAPSED : SIDEBAR_WIDTH,
        },
        transition: theme.transitions.create('width', {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.enteringScreen,
        }),
      }}
    >
      {/* Mobile Drawer */}
      <Drawer
        variant="temporary"
        open={isOpen}
        onClose={toggleDrawer}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: SIDEBAR_WIDTH,
          },
        }}
      >
        {drawerContent}
      </Drawer>

      {/* Desktop Drawer */}
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', md: 'block' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: collapsed ? SIDEBAR_WIDTH_COLLAPSED : SIDEBAR_WIDTH,
            transition: theme.transitions.create('width', {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
            overflowX: 'hidden',
          },
        }}
        open
      >
        {drawerContent}
      </Drawer>
    </Box>
  );
}

export default VirtualPATSidebar;
